export type PreliminaryPathway =
  | 'requires_clinical_review'
  | 'microdose_support'
  | 'comorbidity_dependent'
  | 'standard_titration'

export interface GuidanceContext {
  primaryGoal?: string
  goalMapping?: string
  heightFeet?: number
  heightInches?: number
  weightLbs?: number
  calculatedBmi?: number
  age?: number
  sexAtBirth?: string
  preliminaryPathway?: PreliminaryPathway
  bmiCategory?: string
  hasComorbidities?: boolean
  weightRelatedConditions?: string[]
  glp1Experience?: string
  currentGlp1Med?: string
  pastGlp1Medications?: string[]
  glp1DiscontinuationReason?: string
  glp1Tolerance?: string
  glp1Efficacy?: string
  hadTirzepatide?: boolean
  suggestCarnitide?: boolean
  contraindications?: string[]
  hasCriticalContraindication?: boolean
  hasGiConcerns?: boolean
  hasEatingDisorderFlag?: boolean
}

export interface GuidanceResult {
  preliminaryPathway: PreliminaryPathway
  bmi?: number
  bmiCategory?: string
  productSuggestion: {
    product: 'carnisema' | 'carnitide'
    reason: string
    confidence: 'high' | 'medium' | 'low'
    showCarnitideOption: boolean
  }
  canProceed: boolean
  requiresReview: boolean
  reviewReasons: string[]
  showComorbidityScreen: boolean
  needsClinicalReview: boolean
  clinicianFramedMessage: {
    headline: string
    body: string
    disclaimer: string
  }
}

export function calculateBMI(heightFeet: number, heightInches: number, weightLbs: number): number {
  const totalInches = heightFeet * 12 + heightInches
  if (totalInches <= 0) return 0
  return (weightLbs / (totalInches * totalInches)) * 703
}

export function getBmiCategoryLabel(bmi: number): string {
  if (bmi < 18.5) return 'Underweight'
  if (bmi < 25) return 'Healthy Weight'
  if (bmi < 30) return 'Overweight'
  if (bmi < 35) return 'Obesity Class I'
  if (bmi < 40) return 'Obesity Class II'
  return 'Obesity Class III'
}

export function determinePreliminaryPathway(bmi: number, hasComorbidities?: boolean): PreliminaryPathway {
  if (bmi < 20) return 'requires_clinical_review'
  if (bmi >= 20 && bmi < 27) return 'microdose_support'
  if (bmi >= 27 && bmi < 30) {
    if (hasComorbidities === undefined) return 'comorbidity_dependent'
    return hasComorbidities ? 'standard_titration' : 'requires_clinical_review'
  }
  return 'standard_titration'
}

function getProductSuggestion(context: GuidanceContext): GuidanceResult['productSuggestion'] {
  const currentMed = (context.currentGlp1Med || '').toLowerCase()
  const tirzepatideMeds = ['mounjaro', 'zepbound', 'tirzepatide']
  const isCurrentlyOnTirzepatide = tirzepatideMeds.some((med) => currentMed.includes(med))
  if (isCurrentlyOnTirzepatide || (context.hadTirzepatide && context.suggestCarnitide)) {
    return {
      product: 'carnitide',
      reason: 'Based on your history, CarniTide may be a better fit for clinician review.',
      confidence: 'high',
      showCarnitideOption: false,
    }
  }
  return {
    product: 'carnisema',
    reason: 'CarniSema is the default starting option for many patients.',
    confidence: 'medium',
    showCarnitideOption: true,
  }
}

function collectReviewReasons(context: GuidanceContext, bmi?: number): string[] {
  const reasons: string[] = []
  if (context.hasGiConcerns) reasons.push('GI concerns require clinician review')
  if (context.hasEatingDisorderFlag) reasons.push('Eating disorder history requires coordinated care')
  if (bmi !== undefined && bmi < 20) reasons.push('BMI is below intake threshold for self-serve continuation')
  if (bmi !== undefined && bmi >= 27 && bmi < 30 && context.hasComorbidities === false) {
    reasons.push('BMI 27-29.9 without comorbidity needs clinician confirmation')
  }
  return reasons
}

export function generateGuidance(context: GuidanceContext): GuidanceResult {
  const bmi =
    context.calculatedBmi ||
    (context.heightFeet && context.weightLbs
      ? calculateBMI(context.heightFeet, context.heightInches || 0, context.weightLbs)
      : undefined)
  const bmiCategory = bmi ? getBmiCategoryLabel(bmi) : undefined
  const preliminaryPathway = context.preliminaryPathway || determinePreliminaryPathway(bmi || 30, context.hasComorbidities)
  const reviewReasons = collectReviewReasons(context, bmi)
  const needsClinicalReview = reviewReasons.length > 0 || preliminaryPathway === 'requires_clinical_review'

  return {
    preliminaryPathway,
    bmi,
    bmiCategory,
    productSuggestion: getProductSuggestion(context),
    canProceed: !(bmi !== undefined && bmi < 20),
    requiresReview: needsClinicalReview,
    reviewReasons,
    showComorbidityScreen: preliminaryPathway === 'comorbidity_dependent' && context.hasComorbidities === undefined,
    needsClinicalReview,
    clinicianFramedMessage: {
      headline: 'Clinical review first',
      body: 'Based on your responses, a licensed clinician will confirm the most appropriate approach.',
      disclaimer: 'Individual results vary. Your licensed clinician will confirm your final plan.',
    },
  }
}
